import { defineModule } from '../../engine/module';
import { ClientHeader, ClientHierarchy } from './ClientHeader';
import { OpportunityHeader } from './OpportunityHeader';
import { OpportunityBoard } from './OpportunityBoard';
import { MergeContactAction } from './MergeContactAction';
import { ImportClientsAction } from './ImportClientsAction';
import { ClientTypesField, ContactRolesField, EmailDomainsField } from './widgets';
import './crm.css';

// The CRM module's UI (docs/modules/crm.md): a "Customers" nav section and engine screens
// for clients, contacts, sites, opportunities (list + pipeline board) and activities. Every
// screen is a ScreenDef on the engine; the module adds header blocks (client stats, stage
// rail), the board (built on the engine Board view), the merge / import dialogs and three
// field widgets. No `custom` screens, no engine exceptions.

export const crmModule = defineModule({
  name: 'crm',
  nav: [
    {
      label: 'Customers',
      order: 15,
      items: [
        { label: 'Clients', to: '/clients', icon: 'building', key: 'k' },
        { label: 'Contacts', to: '/contacts', icon: 'user', key: 'o' },
        { label: 'Sites', to: '/sites', icon: 'map-pin' },
        { label: 'Opportunities', to: '/opportunities', icon: 'target', key: 'p' },
        { label: 'Activities', to: '/activities', icon: 'activity', key: 'a' },
      ],
    },
    {
      label: 'Customers & sales',
      area: 'config',
      order: 20,
      items: [
        { label: 'Client types', to: '/client-types', icon: 'tag' },
        { label: 'Contact roles', to: '/contact-roles', icon: 'users' },
        { label: 'Territories', to: '/territories', icon: 'globe' },
        { label: 'Pipelines', to: '/pipelines', icon: 'workflow' },
      ],
    },
  ],
  screens: [
    // Configuration → Customers & sales: the CRM catalogs (generic engine screens).
    { path: '/client-types', resource: 'client-types', kind: 'list', title: 'Client types', subtitle: 'Tenant-defined tags a client can carry (customer, prospect, vendor…).', columns: ['name', 'color', 'sort_order', 'is_active'], query: { sort: 'sort_order' }, inspector: true },
    { path: '/client-types/:id', resource: 'client-types', kind: 'detail', builtinTabs: ['overview', 'activity'] },
    { path: '/contact-roles', resource: 'contact-roles', kind: 'list', title: 'Contact roles', subtitle: 'What a contact is to the client — billing, technical, executive, on-site.', columns: ['code', 'name', 'sort_order', 'is_active'], query: { sort: 'sort_order' }, inspector: true },
    { path: '/contact-roles/:id', resource: 'contact-roles', kind: 'detail', builtinTabs: ['overview', 'activity'] },
    { path: '/territories', resource: 'territories', kind: 'list', title: 'Territories', subtitle: 'Regions or books of business, each with an owning technician.', columns: ['name', 'owner_tech_id', 'is_default'], query: { sort: 'name', expand: ['owner_tech'] }, inspector: true },
    { path: '/territories/:id', resource: 'territories', kind: 'detail', builtinTabs: ['overview', 'activity'] },
    { path: '/pipelines', resource: 'pipelines', kind: 'list', title: 'Pipelines', subtitle: 'Sales pipelines and their stages; the default pipeline receives new opportunities.', columns: ['name', 'is_default', 'is_active'], query: { sort: 'name' } },
    { path: '/pipelines/:id', resource: 'pipelines', kind: 'detail', builtinTabs: ['overview', 'activity'], tabs: [{ key: 'stages', label: 'Stages', icon: 'milestone', resource: 'pipeline-stages', filter: { pipeline_id: ':id' }, columns: ['sort_order', 'name', 'probability', 'is_won', 'is_lost', 'color'] }] },
    // ── Clients ──────────────────────────────────────────────────────────
    {
      path: '/clients',
      resource: 'clients',
      kind: 'list',
      title: 'Clients',
      subtitle: 'Companies you serve, sell to, or buy from',
      columns: ['ref', 'name', 'kind', 'status', 'client_type_ids', 'industry', 'account_manager_tech_id', 'phone', 'currency'],
      filters: [{ field: 'status', kind: 'chips' }, { field: 'kind', kind: 'chips' }],
      query: { sort: 'name', expand: ['account_manager_tech', 'territory'] },
      actions: [{ name: 'import', label: 'Import CSV', scope: 'collection', component: ImportClientsAction, placement: 'toolbar' }],
    },
    {
      path: '/clients/:id',
      resource: 'clients',
      kind: 'detail',
      header: ClientHeader,
      actions: [{ name: 'convert', when: { kind: ['prospect'] } }],
      tabs: [
        { key: 'sites', label: 'Sites', icon: 'map-pin', resource: 'sites', filter: { client_id: ':id' }, columns: ['name', 'city', 'country', 'status', 'is_primary', 'primary_contact_id'] },
        { key: 'contacts', label: 'Contacts', icon: 'users', resource: 'contacts', filter: { client_id: ':id' }, columns: ['name', 'title', 'email', 'phone', 'status', 'contact_role_ids', 'is_primary'] },
        { key: 'opportunities', label: 'Opportunities', icon: 'target', resource: 'opportunities', filter: { client_id: ':id' }, columns: ['ref', 'name', 'stage_id', 'status', 'probability', 'one_time_minor', 'recurring_minor', 'expected_close'] },
        { key: 'contracts', label: 'Contracts', icon: 'file-check', resource: 'contracts', filter: { billing_party_id: ':billing_party_id' }, columns: ['ref', 'status', 'cycle', 'period_charge_minor', 'next_invoice_date'] },
        { key: 'invoices', label: 'Invoices', icon: 'receipt', resource: 'invoices', filter: { billing_party_id: ':billing_party_id' }, columns: ['number', 'status', 'issue_date', 'total', 'outstanding'] },
        { key: 'tickets', label: 'Tickets', icon: 'ticket', resource: 'tickets', filter: { client_id: ':id' }, columns: ['ref', 'summary', 'status', 'assigned_tech_id', 'opened_at'] },
        { key: 'time', label: 'Time', icon: 'timer', resource: 'time-entries', filter: { client_id: ':id' }, columns: ['occurred_on', 'technician_id', 'minutes', 'charge_type', 'note'] },
        { key: 'crm-activity', label: 'CRM activity', icon: 'phone', resource: 'crm-activities', filter: { client_id: ':id' }, columns: ['kind', 'subject', 'contact_id', 'owner_tech_id', 'due_at', 'done_at'] },
        // Per-account portal features: only rows that differ from "included and on" (docs/modules/portal.md).
        { key: 'portal-features', label: 'Portal features', icon: 'toggle-left', resource: 'portal-client-features', filter: { client_id: ':id' }, columns: ['feature', 'entitled', 'enabled', 'reason', 'updated_at'] },
        { key: 'hierarchy', label: 'Hierarchy', icon: 'list-tree', component: ClientHierarchy },
      ],
    },

    // ── Contacts ─────────────────────────────────────────────────────────
    {
      path: '/contacts',
      resource: 'contacts',
      kind: 'list',
      title: 'Contacts',
      subtitle: 'The people at your clients',
      columns: ['name', 'title', 'client_id', 'email', 'phone', 'status', 'contact_role_ids', 'is_primary', 'portal_access_level'],
      filters: [{ field: 'status', kind: 'chips' }],
      query: { sort: 'name', expand: ['client', 'site'] },
    },
    {
      path: '/contacts/:id',
      resource: 'contacts',
      kind: 'detail',
      actions: [{ name: 'merge', component: MergeContactAction, placement: 'primary' }],
      tabs: [
        { key: 'opportunities', label: 'Opportunities', icon: 'target', resource: 'opportunities', filter: { contact_id: ':id' }, columns: ['ref', 'name', 'stage_id', 'status', 'one_time_minor', 'expected_close'] },
        { key: 'crm-activity', label: 'CRM activity', icon: 'phone', resource: 'crm-activities', filter: { contact_id: ':id' }, columns: ['kind', 'subject', 'owner_tech_id', 'due_at', 'done_at'] },
        { key: 'tickets', label: 'Tickets', icon: 'ticket', resource: 'tickets', filter: { contact_id: ':id' }, columns: ['ref', 'summary', 'status', 'opened_at'] },
      ],
    },

    // ── Sites ────────────────────────────────────────────────────────────
    {
      path: '/sites',
      resource: 'sites',
      kind: 'list',
      title: 'Sites',
      subtitle: 'Client locations',
      columns: ['name', 'client_id', 'city', 'region', 'country', 'status', 'is_primary', 'is_invoice_site', 'primary_contact_id'],
      filters: [{ field: 'status', kind: 'chips' }],
      query: { sort: 'name', expand: ['client', 'primary_contact'] },
    },
    {
      path: '/sites/:id',
      resource: 'sites',
      kind: 'detail',
      tabs: [
        { key: 'contacts', label: 'Contacts', icon: 'users', resource: 'contacts', filter: { site_id: ':id' }, columns: ['name', 'title', 'email', 'phone', 'status'] },
        { key: 'tickets', label: 'Tickets', icon: 'ticket', resource: 'tickets', filter: { client_id: ':client_id' }, columns: ['ref', 'summary', 'status', 'opened_at'] },
      ],
    },

    // ── Opportunities ────────────────────────────────────────────────────
    {
      path: '/opportunities',
      resource: 'opportunities',
      kind: 'list',
      title: 'Opportunities',
      subtitle: 'Deals in flight — switch to the board to work the pipeline',
      columns: ['ref', 'name', 'client_id', 'stage_id', 'status', 'probability', 'one_time_minor', 'recurring_minor', 'expected_close', 'owner_tech_id'],
      filters: [{ field: 'status', kind: 'chips' }],
      query: { sort: '-created_at', expand: ['client', 'stage', 'owner_tech'] },
      actions: [
        { name: 'board', label: 'Board', scope: 'collection', placement: 'toolbar', run: ({ navigate }) => navigate('/opportunities/board') },
        { name: 'move-stage', hidden: true },
      ],
    },
    { path: '/opportunities/board', resource: 'opportunities', kind: 'board', title: 'Pipeline', component: OpportunityBoard, board: { groupBy: 'stage_id' } },
    {
      path: '/opportunities/:id',
      resource: 'opportunities',
      kind: 'detail',
      header: OpportunityHeader,
      query: { expand: ['client', 'contact', 'stage', 'owner_tech'] },
      actions: [
        { name: 'win', when: { status: ['open', 'inactive'] } },
        { name: 'lose', when: { status: ['open', 'inactive'] } },
        { name: 'move-stage', hidden: true },
      ],
      tabs: [
        { key: 'crm-activity', label: 'CRM activity', icon: 'phone', resource: 'crm-activities', filter: { opportunity_id: ':id' }, columns: ['kind', 'subject', 'contact_id', 'owner_tech_id', 'due_at', 'done_at'] },
        { key: 'history', label: 'Stage history', icon: 'history', resource: 'opportunity-stage-history', filter: { opportunity_id: ':id' }, columns: ['created_at', 'from_stage_id', 'to_stage_id', 'note', 'changed_by'] },
      ],
    },

    // ── Activities ───────────────────────────────────────────────────────
    {
      path: '/activities',
      resource: 'crm-activities',
      kind: 'list',
      title: 'Activities',
      subtitle: 'Calls, emails, meetings, tasks and notes',
      columns: ['kind', 'subject', 'client_id', 'contact_id', 'owner_tech_id', 'due_at', 'done_at'],
      filters: [{ field: 'kind', kind: 'chips' }],
      views: ['table', 'calendar'],
      calendar: { dateField: 'due_at' },
      query: { sort: '-created_at', expand: ['client', 'contact', 'owner_tech'] },
    },
    { path: '/activities/:id', resource: 'crm-activities', kind: 'detail' },
  ],
  widgets: {
    'email-domains': EmailDomainsField,
    'client-types': ClientTypesField,
    'contact-roles': ContactRolesField,
  },
  commands: [
    { id: 'new-client', label: 'New client…', kind: 'create', icon: 'building', keywords: 'company customer prospect', run: ({ navigate }) => navigate('/clients?new=1') },
    { id: 'new-opportunity', label: 'New opportunity…', kind: 'create', icon: 'target', keywords: 'deal pipeline sale', run: ({ navigate }) => navigate('/opportunities?new=1') },
    { id: 'log-activity', label: 'Log activity…', kind: 'create', icon: 'phone', keywords: 'call meeting note task email', run: ({ navigate }) => navigate('/activities?new=1') },
    { id: 'pipeline-board', label: 'Pipeline board', kind: 'go to', icon: 'kanban', keywords: 'opportunities deals kanban', run: ({ navigate }) => navigate('/opportunities/board') },
  ],
});
